import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { SiteHeader, PlatformFooter } from "../platform/PlatformChrome";
import { BlockRenderer } from "../editor/BlockRenderer";
import { lessonEditorConfig } from "../editor/courseConfig";
import { MaterialList } from "./MaterialList";
import { useCourse, useCourseProgress, useLesson, useSaveLessonProgress } from "../../lib/courses";
import { useMe } from "../../lib/auth";
import type { PublicCourse, PublicCourseItem } from "@vedaunion/shared";

/**
 * Lesson reading view — `/courses/:slug/lessons/:lessonId` (public) and the
 * dashboard equivalent.
 *
 * Layout: a single constrained reading column by default. When (and ONLY when)
 * the lesson has materials, they render as a right rail on wide screens and as
 * a section BELOW the content on mobile. No floating TOC, no dead right column,
 * nothing that mounts and never unmounts.
 *
 * Actions: a sticky bottom bar (Prev / Next / Mark complete) within thumb reach
 * on phone + tablet, and an inline footer on wide screens. Mark complete
 * persists progress and flips to "Completed ✓"; an in_progress ping fires once
 * on mount for logged-in users.
 */

export default function LessonPlayer({ surface }: { surface: "public" | "dashboard" }) {
  const { slug, lessonId: lessonIdParam } = useParams<{ slug: string; lessonId: string }>();
  const lessonId = lessonIdParam ? Number(lessonIdParam) : undefined;

  const body =
    !slug || !lessonId || Number.isNaN(lessonId) ? (
      <div className="course-shell">
        <p className="course-meta">No lesson specified.</p>
      </div>
    ) : (
      <LessonBody slug={slug} lessonId={lessonId} />
    );

  if (surface === "public") {
    return (
      <>
        <SiteHeader />
        <main className="course-surface pt-24 sm:pt-28 pb-28 lg:pb-16">{body}</main>
        <PlatformFooter />
      </>
    );
  }
  return <div className="course-surface py-2 pb-28 lg:pb-2">{body}</div>;
}

function LessonBody({ slug, lessonId }: { slug: string; lessonId: number }) {
  const location = useLocation();
  const me = useMe();
  const user = me.data?.user ?? null;

  const lessonQ = useLesson(lessonId);
  const courseQ = useCourse(slug);
  const lesson = lessonQ.data?.lesson;
  const course = courseQ.data?.course;

  const saveProgress = useSaveLessonProgress(lessonId, slug);
  const progressQ = useCourseProgress(slug, !!user);

  const visitFiredFor = useRef<number | null>(null);
  const [justCompleted, setJustCompleted] = useState(false);

  // Whether the server already records this lesson as completed.
  const persistedComplete = useMemo(
    () =>
      (progressQ.data?.progress?.lessons ?? []).some(
        (l) => l.lessonId === lessonId && l.status === "completed",
      ),
    [progressQ.data, lessonId],
  );
  const isComplete = justCompleted || persistedComplete;

  // Fire an in_progress visit exactly once per lesson, for logged-in users.
  useEffect(() => {
    if (!user) return;
    if (visitFiredFor.current === lessonId) return;
    visitFiredFor.current = lessonId;
    saveProgress.mutateAsync({ status: "in_progress", lastPosition: null }).catch(() => {
      visitFiredFor.current = null; // allow a retry on next render
    });
  }, [user, lessonId, saveProgress]);

  // Reset transient UI when the lesson changes.
  useEffect(() => {
    setJustCompleted(false);
  }, [lessonId]);

  const { prev, next } = useMemo(() => neighbours(course, lessonId), [course, lessonId]);

  if (lessonQ.isLoading) {
    return (
      <div className="course-shell">
        <p className="course-meta">Loading…</p>
      </div>
    );
  }
  if (!lesson) {
    return (
      <div className="course-shell">
        <h1 className="course-h2 mb-3">Lesson not found.</h1>
        <Link to={`/courses/${slug}`} className="course-back">
          ← Back to course
        </Link>
      </div>
    );
  }

  const materials = lesson.materials;
  const hasMaterials = materials.length > 0;

  const markComplete = async () => {
    if (!user) return;
    try {
      await saveProgress.mutateAsync({ status: "completed", lastPosition: null });
      setJustCompleted(true);
    } catch {
      // surfaced by the button staying actionable; no overlay
    }
  };

  const loginHref = `/login?next=${encodeURIComponent(location.pathname)}`;

  return (
    <div className="course-shell">
      <Link to={`/courses/${slug}`} className="course-back mb-5">
        ← {course?.title ?? "Back to course"}
      </Link>

      <div className={`lesson-grid ${hasMaterials ? "has-rail" : ""}`}>
        {/* Reading column */}
        <article className="min-w-0">
          <header className={hasMaterials ? "mb-6" : "course-measure mb-6"}>
            <p className="course-eyebrow course-eyebrow-violet mb-2">
              Lesson
              {lesson.estimatedMinutes ? ` · ${lesson.estimatedMinutes} min` : ""}
            </p>
            <h1 className="course-title">{lesson.title}</h1>
            {lesson.subtitle && <p className="course-subtitle mt-3">{lesson.subtitle}</p>}
          </header>

          <div className={`lesson-prose ${hasMaterials ? "" : "course-measure"}`}>
            <BlockRenderer
              doc={lesson.contentJson}
              config={lessonEditorConfig}
              htmlFallback={lesson.contentHtml}
            />
          </div>

          {/* Materials BELOW the content on mobile/tablet (rail takes over ≥lg). */}
          {hasMaterials && (
            <section className="lg:hidden mt-9 pt-7 border-t border-[var(--color-rule)]">
              <p className="course-eyebrow course-eyebrow-violet mb-3">Materials</p>
              <MaterialList materials={materials} variant="full" />
            </section>
          )}

          {/* Inline footer nav — wide screens only (mobile uses the sticky bar).
              Back on the left; the main actions (complete + Next) on the right,
              with Next as the primary call-to-action. */}
          <div className="hidden lg:flex items-center justify-between gap-4 mt-10 pt-6 border-t border-[var(--color-rule)]">
            {prev ? (
              <Link to={navHref(slug, prev)} className="course-cta-ghost max-w-[20rem]">
                <span aria-hidden>←</span>
                <span className="truncate">{labelFor(prev)}</span>
              </Link>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              {user ? (
                <button
                  type="button"
                  onClick={markComplete}
                  disabled={saveProgress.isPending || isComplete}
                  className={`course-cta-ghost ${isComplete ? "lesson-complete-done" : ""}`}
                >
                  {isComplete ? "Completed ✓" : saveProgress.isPending ? "Saving…" : "Mark complete"}
                </button>
              ) : (
                <Link to={loginHref} className="course-cta-ghost">
                  Log in to track progress
                </Link>
              )}
              {next && (
                <Link to={navHref(slug, next)} className="course-cta max-w-[22rem]">
                  <span className="truncate">{labelFor(next)}</span>
                  <span aria-hidden>→</span>
                </Link>
              )}
            </div>
          </div>
        </article>

        {/* Materials — right rail on wide screens, ONLY when present. */}
        {hasMaterials && (
          <aside className="hidden lg:block">
            <div className="lesson-rail-sticky">
              <p className="course-eyebrow course-eyebrow-violet mb-3">Materials</p>
              <MaterialList materials={materials} variant="rail" />
            </div>
          </aside>
        )}
      </div>

      {/* Sticky bottom action bar — phone + tablet only. */}
      <div className="lg:hidden lesson-actionbar">
        <div className="lesson-actionbar-inner">
          <MobileNavButton slug={slug} item={prev} direction="prev" />
          {user ? (
            <button
              type="button"
              onClick={markComplete}
              disabled={saveProgress.isPending || isComplete}
              className={`flex-1 btn-app justify-center min-h-[44px] ${
                isComplete ? "lesson-complete-done" : ""
              }`}
            >
              {isComplete ? "Completed ✓" : saveProgress.isPending ? "Saving…" : "Mark complete"}
            </button>
          ) : (
            <Link
              to={loginHref}
              className="flex-1 btn-app-ghost justify-center min-h-[44px] text-center"
            >
              Log in to track
            </Link>
          )}
          <MobileNavButton slug={slug} item={next} direction="next" />
        </div>
      </div>
    </div>
  );
}

function MobileNavButton({
  slug,
  item,
  direction,
}: {
  slug: string;
  item: PublicCourseItem | null;
  direction: "prev" | "next";
}) {
  if (!item) {
    return <span className="flex-none w-11" aria-hidden />;
  }
  return (
    <Link
      to={navHref(slug, item)}
      aria-label={direction === "prev" ? "Previous" : "Next"}
      className="flex-none grid place-items-center btn-app-ghost min-h-[44px] w-11 px-0"
    >
      {direction === "prev" ? "←" : "→"}
    </Link>
  );
}

function labelFor(item: PublicCourseItem): string {
  if (item.kind === "lesson") return item.lesson?.title ?? "Lesson";
  return item.quiz?.title ?? "Exercise set";
}

function navHref(slug: string, item: PublicCourseItem): string {
  if (item.kind === "lesson" && item.lesson) return `/courses/${slug}/lessons/${item.lesson.id}`;
  if (item.kind === "quiz" && item.quiz) return `/courses/${slug}/quizzes/${item.quiz.id}`;
  return `/courses/${slug}`;
}

/** Find the prev/next course items relative to the current lesson. */
function neighbours(
  course: PublicCourse | undefined,
  lessonId: number,
): { prev: PublicCourseItem | null; next: PublicCourseItem | null } {
  if (!course) return { prev: null, next: null };
  const idx = course.items.findIndex((i) => i.kind === "lesson" && i.lesson?.id === lessonId);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? course.items[idx - 1]! : null,
    next: idx < course.items.length - 1 ? course.items[idx + 1]! : null,
  };
}
